import { useAuthContext } from "@/context/AuthContext";
import ProfielBreadCrumbsV2 from "./ProfielBreadCrumbsV2";
import { useCallback, useMemo } from "react";
import { useContentSelectieContext } from "@/context/ContentSelectieContext";
import { ProfielJaar, ProfielMethode } from "@/lib/profiel_types";
import SelectionPill from "./SelectionPill";
import { getConfigForMethode, getOptionsForMethode } from "@/lib/niveau_config";
import HoofdstukSelectV2 from "./HoofdstukSelectV2";
import type { ContentSelectFlow } from "./types";

export default function ProfielSelectV2({
  hideTitle = false,
  hideBreadcrumbs = false,
  flow = "full"
}: {
  hideTitle?: boolean;
  hideBreadcrumbs?: boolean;
  flow?: ContentSelectFlow;
}) {
  const { isParent } = useAuthContext();
  const {
    selectedJaar,
    selectedMethode,
    selectedNiveau,
    selectedEdition,
    selectedWiskundeType,
    setSelectedJaar,
    setSelectedMethode,
    setSelectedNiveau,
    setSelectedEdition,
    setSelectedWiskundeType,
    profiel
  } = useContentSelectieContext();

  const isBovenbouw = (selectedJaar ?? 0) > 3;

  const step = useMemo(() => {
    if (!selectedJaar) return "jaar";
    if (!selectedMethode) return "methode";
    if (!selectedNiveau) return "niveau";
    if (!selectedEdition) return "editie";
    return "hoofdstuk";
  }, [selectedJaar, selectedMethode, selectedNiveau, selectedEdition]);

  const selectMethode = useCallback(
    (methode: ProfielMethode) => {
      if (!selectedJaar) return;

      setSelectedMethode(methode);

      const config = getConfigForMethode(methode);
      const autoEdition = config.autoEditie?.[selectedJaar];
      if (autoEdition) {
        setSelectedEdition(autoEdition);
      }
    },
    [setSelectedMethode, selectedJaar, setSelectedEdition]
  );

  const niveauOptions = selectedMethode
    ? getOptionsForMethode(selectedMethode).filter((o) =>
        o.jaren.includes(selectedJaar!)
      )
    : [];

  const onderbouwOptions = niveauOptions.filter((o) => !o.wiskundeType);
  const bovenbouwOptions = niveauOptions.filter((o) => !!o.wiskundeType);

  return (
    <div className="px-4 md:px-8">
      {!hideTitle && (
        <div className="text-center mb-8 mt-4">
          {isParent && flow === "quickstart" ? (
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Zelf even meekijken?
              </h2>
              <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Ontdek hoe WisWiz jouw kind helpt door zelf een hoofdstuk te
                proberen.
              </p>
              <p className="text-base md:text-lg text-gray-500 italic mt-2">
                Of om je eigen wiskunde weer even aan te scherpen
              </p>
            </div>
          ) : (
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Aan de slag met WisWiz
            </h1>
          )}
        </div>
      )}

      {(selectedMethode || selectedJaar) && (
        <div className={hideBreadcrumbs ? "md:hidden" : ""}>
          <ProfielBreadCrumbsV2 />
        </div>
      )}

      <p className="text-gray-900 text-xl md:text-2xl font-bold text-center mt-6">
        {step === "jaar" && "In welke klas zit je?"}
        {step === "methode" && "Welke methode heb je op school?"}
        {step === "niveau" &&
          (isBovenbouw
            ? "Wat is je wiskundeprofiel?"
            : "Welk schooltype staat er op de kaft van je lesboek?")}
        {step === "editie" &&
          "Welke editie staat op de eerste pagina van je lesboek?"}
        {step === "hoofdstuk" && "Met welk hoofdstuk ben je bezig?"}
      </p>

      <div className="w-full mt-8">
        {/* JAAR */}
        {step === "jaar" && (
          <div className="grid grid-cols-3 justify-items-center gap-4 max-w-md mx-auto">
            {([1, 2, 3, 4, 5, 6] as ProfielJaar[]).map((jaar) => (
              <SelectionPill
                key={jaar}
                onClick={() => {
                  setSelectedJaar(jaar);
                  setSelectedNiveau(undefined);
                  setSelectedWiskundeType(undefined);

                  // If methode is already set (e.g. from uitproberen),
                  // preserve it and apply autoEditie
                  if (selectedMethode) {
                    const config = getConfigForMethode(selectedMethode);
                    const autoEdition = config.autoEditie?.[jaar];
                    setSelectedEdition(autoEdition ?? undefined);
                  } else {
                    setSelectedEdition(undefined);
                  }
                }}
                ariaLabel={`Selecteer klas ${jaar}`}
              >
                Klas {jaar}
              </SelectionPill>
            ))}
          </div>
        )}

        {/* METHODE */}
        {step === "methode" && (
          <div className="flex flex-wrap justify-center gap-4">
            <SelectionPill
              onClick={() => selectMethode("Getal & Ruimte")}
              ariaLabel="Selecteer Getal & Ruimte"
            >
              Getal & Ruimte
            </SelectionPill>
            <SelectionPill
              onClick={() => selectMethode("Moderne Wiskunde")}
              ariaLabel="Selecteer Moderne Wiskunde"
            >
              Moderne Wiskunde
            </SelectionPill>
          </div>
        )}

        {/* NIVEAU */}
        {step === "niveau" && (
          <div className="flex flex-wrap justify-center gap-4">
            {!isBovenbouw
              ? onderbouwOptions.map((o) => (
                  <SelectionPill
                    key={o.label}
                    onClick={() => {
                      setSelectedNiveau(o.niveau);
                      if (o.autoEditie) setSelectedEdition(o.autoEditie);
                    }}
                    ariaLabel={`Selecteer ${o.label}`}
                  >
                    {o.label}
                  </SelectionPill>
                ))
              : [
                  ...bovenbouwOptions.map((o) => (
                    <SelectionPill
                      key={o.label}
                      onClick={() => {
                        setSelectedNiveau(o.niveau);
                        if (o.wiskundeType)
                          setSelectedWiskundeType(o.wiskundeType);
                        if (o.autoEditie) setSelectedEdition(o.autoEditie);
                      }}
                      ariaLabel={`Selecteer ${o.label}`}
                    >
                      {o.label}
                    </SelectionPill>
                  )),
                  ...onderbouwOptions.map((o) => (
                    <SelectionPill
                      key={o.label}
                      onClick={() => {
                        setSelectedNiveau(o.niveau);
                        if (o.autoEditie) setSelectedEdition(o.autoEditie);
                      }}
                      ariaLabel={`Selecteer ${o.label}`}
                    >
                      {o.label}
                    </SelectionPill>
                  ))
                ]}
          </div>
        )}

        {/* EDITIE */}
        {step === "editie" && (
          <div className="flex flex-wrap justify-center gap-4">
            <SelectionPill
              onClick={() => setSelectedEdition(12)}
              ariaLabel="Selecteer Editie 12"
            >
              <div className="text-center">
                <div className="font-bold">Editie 12</div>
                <div className="text-xs text-gray-500 mt-1">Oudere editie</div>
              </div>
            </SelectionPill>
            <SelectionPill
              onClick={() => setSelectedEdition(13)}
              ariaLabel="Selecteer Editie 13"
            >
              <div className="text-center">
                <div className="font-bold">Editie 13</div>
                <div className="text-xs text-gray-500 mt-1">
                  Nieuwste editie
                </div>
              </div>
            </SelectionPill>
          </div>
        )}

        {/* HOOFDSTUK */}
        {step === "hoofdstuk" && <HoofdstukSelectV2 flow={flow} />}
      </div>
    </div>
  );
}
